import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPost, ApiRequestError } from "../../lib/api";
import { formatAmountInputToCents, formatCurrency } from "../../lib/format";
import { mapZodErrors } from "../../lib/forms";
import { loadPaypalSdk } from "../../lib/paypal";
import { usePublicConfig } from "../../lib/public-config";
import { donationSchema } from "../../shared/schemas";
import { Button } from "../ui/Button";
import { InputField } from "../ui/FormField";
import { StatusBanner } from "../ui/StatusBanner";
import { TurnstileField } from "../forms/TurnstileField";

const presetAmounts = [25, 50, 100, 250, 500];

interface DonationFormState {
  donorName: string;
  donorEmail: string;
  selectedAmount: string;
  customAmount: string;
  provider: "stripe" | "paypal";
  allowPublicRecognition: boolean;
  publicDisplayName: string;
  website: string;
  turnstileToken: string;
}

const initialState: DonationFormState = {
  donorName: "",
  donorEmail: "",
  selectedAmount: "100",
  customAmount: "",
  provider: "stripe",
  allowPublicRecognition: false,
  publicDisplayName: "",
  website: "",
  turnstileToken: "",
};

const resolveAmount = (values: DonationFormState): number | null =>
  values.selectedAmount === "custom"
    ? formatAmountInputToCents(values.customAmount)
    : Math.round(Number(values.selectedAmount) * 100);

export const DonationForm = () => {
  const navigate = useNavigate();
  const { config, loading: configLoading, error: configError } = usePublicConfig();
  const [values, setValues] = useState<DonationFormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [paypalError, setPaypalError] = useState<string | null>(null);
  const paypalContainerRef = useRef<HTMLDivElement | null>(null);
  const latestValuesRef = useRef(values);

  latestValuesRef.current = values;

  const setFieldValue = <K extends keyof DonationFormState>(field: K, value: DonationFormState[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field as string];
      if (field === "selectedAmount" || field === "customAmount") {
        delete next.amount;
      }
      if (field === "allowPublicRecognition" && value === false) {
        delete next.publicDisplayName;
      }
      return next;
    });
    setFormError(null);
    setPaypalError(null);
  };

  const buildPayload = (provider: "stripe" | "paypal") => {
    const amount = resolveAmount(latestValuesRef.current);

    if (!amount) {
      setErrors((current) => ({ ...current, amount: "Enter a valid donation amount." }));
      return null;
    }

    if (config?.turnstileSiteKey && !latestValuesRef.current.turnstileToken) {
      setFormError("Please complete the verification challenge before continuing.");
      return null;
    }

    const payload = {
      donorName: latestValuesRef.current.donorName,
      donorEmail: latestValuesRef.current.donorEmail,
      amount,
      provider,
      allowPublicRecognition: latestValuesRef.current.allowPublicRecognition,
      publicDisplayName: latestValuesRef.current.allowPublicRecognition
        ? latestValuesRef.current.publicDisplayName
        : "",
      website: latestValuesRef.current.website,
      turnstileToken: latestValuesRef.current.turnstileToken,
    };

    const parsed = donationSchema.safeParse(payload);

    if (!parsed.success) {
      setErrors(mapZodErrors(parsed.error));
      return null;
    }

    return parsed.data;
  };

  const redirectToStripe = async () => {
    const payload = buildPayload("stripe");

    if (!payload) {
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      const response = await apiPost<{ ok: true; data: { checkoutUrl: string } }>("/api/donations/stripe/session", payload);
      window.location.assign(response.data.checkoutUrl);
    } catch (error) {
      if (error instanceof ApiRequestError) {
        setErrors(error.fieldErrors ?? {});
        setFormError(error.message);
      } else {
        setFormError("Stripe checkout could not be started right now.");
      }
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let active = true;
    let buttons: { close?: () => Promise<void> | void } | null = null;

    if (values.provider !== "paypal" || !config?.paypalClientId || !paypalContainerRef.current) {
      if (paypalContainerRef.current) {
        paypalContainerRef.current.innerHTML = "";
      }
      return;
    }

    loadPaypalSdk(config.paypalClientId, config.currency)
      .then(async () => {
        if (!active || !paypalContainerRef.current || !window.paypal) {
          return;
        }

        paypalContainerRef.current.innerHTML = "";
        const instance = window.paypal.Buttons({
          style: {
            layout: "vertical",
            color: "gold",
            shape: "rect",
            label: "paypal",
          },
          createOrder: async () => {
            const payload = buildPayload("paypal");

            if (!payload) {
              throw new Error("Donation details are incomplete.");
            }

            setSubmitting(true);
            setFormError(null);

            try {
              const response = await apiPost<{ ok: true; data: { orderId: string } }>("/api/donations/paypal/order", payload);
              return response.data.orderId;
            } catch (error) {
              if (error instanceof ApiRequestError) {
                setErrors(error.fieldErrors ?? {});
                setFormError(error.message);
              } else {
                setFormError("PayPal checkout could not be started right now.");
              }
              setSubmitting(false);
              throw error;
            }
          },
          onApprove: async (data: { orderID: string }) => {
            setSubmitting(true);
            setFormError(null);

            try {
              await apiPost<{ ok: true; data: { status: string } }>("/api/donations/paypal/capture", {
                orderId: data.orderID,
              });
              navigate(`/thank-you?provider=paypal&order_id=${encodeURIComponent(data.orderID)}`);
            } catch (error) {
              setFormError(
                error instanceof ApiRequestError
                  ? error.message
                  : "PayPal approved the payment, but confirmation could not be completed yet.",
              );
            } finally {
              setSubmitting(false);
            }
          },
          onCancel: () => {
            navigate("/donate?status=cancelled");
          },
          onError: () => {
            setFormError("PayPal could not be reached. Please try again or choose Stripe.");
            setSubmitting(false);
          },
        });

        buttons = instance;

        if (instance.isEligible && !instance.isEligible()) {
          setPaypalError("PayPal is not available in this browser context.");
          return;
        }

        await instance.render(paypalContainerRef.current);
        setPaypalError(null);
      })
      .catch((error) => {
        if (!active) {
          return;
        }

        setPaypalError(error instanceof Error ? error.message : "PayPal could not be loaded.");
      });

    return () => {
      active = false;
      if (paypalContainerRef.current) {
        paypalContainerRef.current.innerHTML = "";
      }
      buttons?.close?.();
    };
  }, [config?.currency, config?.paypalClientId, navigate, values.provider]);

  const amountInCents = resolveAmount(values);
  const stripeConfigured = config?.stripeConfigured ?? false;
  const paypalConfigured = config?.paypalConfigured ?? false;

  useEffect(() => {
    if (!stripeConfigured && paypalConfigured) {
      setValues((current) => ({ ...current, provider: "paypal" }));
    }
  }, [paypalConfigured, stripeConfigured]);

  return (
    <div className="space-y-12 max-w-[650px]">
      {config && !stripeConfigured && !paypalConfigured ? (
        <StatusBanner
          tone="neutral"
          title="Online donations are not configured on this deployment"
          message="Stripe and PayPal credentials have not been added to this live Worker yet, so checkout is currently disabled."
        />
      ) : null}

      <div className="space-y-8">
        <InputField
          id="donor-name"
          label="Full name"
          autoComplete="name"
          value={values.donorName}
          onChange={(event) => setFieldValue("donorName", event.target.value)}
          error={errors.donorName}
        />
        <InputField
          id="donor-email"
          label="Email"
          type="email"
          autoComplete="email"
          value={values.donorEmail}
          onChange={(event) => setFieldValue("donorEmail", event.target.value)}
          error={errors.donorEmail}
        />
      </div>

      <fieldset className="space-y-3">
        <legend className="block text-sm font-semibold text-ink">Donation amount</legend>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {presetAmounts.map((amount) => {
            const selected = values.selectedAmount === String(amount);

            return (
              <button
                key={amount}
                type="button"
                className={`rounded-sm border px-4 py-4 text-left transition ${
                  selected ? "border-burgundy bg-burgundy text-white" : "border-stone-300 bg-white text-ink"
                }`}
                onClick={() => setFieldValue("selectedAmount", String(amount))}
              >
                <span className="block text-lg font-semibold">{formatCurrency(amount * 100)}</span>
                <span className={`mt-1 block text-sm ${selected ? "text-white/80" : "text-stone-600"}`}>
                  Secure online donation
                </span>
              </button>
            );
          })}
        </div>
        <div className="grid gap-4 md:grid-cols-[200px_1fr] md:items-end">
          <button
            type="button"
            className={`rounded-sm border px-4 py-4 text-left transition ${
              values.selectedAmount === "custom"
                ? "border-burgundy bg-burgundy text-white"
                : "border-stone-300 bg-white text-ink"
            }`}
            onClick={() => setFieldValue("selectedAmount", "custom")}
          >
            <span className="block text-lg font-semibold">Custom amount</span>
            <span className={`mt-1 block text-sm ${values.selectedAmount === "custom" ? "text-white/80" : "text-stone-600"}`}>
              Choose a precise amount
            </span>
          </button>
          <InputField
            id="custom-amount"
            label="Custom amount in USD"
            inputMode="decimal"
            placeholder="100.00"
            value={values.customAmount}
            onChange={(event) => setFieldValue("customAmount", event.target.value)}
            error={errors.amount}
            disabled={values.selectedAmount !== "custom"}
          />
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="block text-sm font-semibold text-ink">Payment method</legend>
        <div className="grid gap-3 md:grid-cols-2">
          {[
            {
              value: "stripe" as const,
              title: "Stripe checkout",
              body: "You will be redirected to Stripe’s hosted checkout for card payment processing.",
              enabled: stripeConfigured,
            },
            {
              value: "paypal" as const,
              title: "PayPal",
              body: "Use the PayPal JavaScript checkout flow and complete capture on the server.",
              enabled: paypalConfigured,
            },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              className={`rounded-sm border px-5 py-5 text-left transition ${
                values.provider === option.value
                  ? "border-burgundy bg-white shadow-sm ring-1 ring-burgundy"
                  : "border-stone-300 bg-transparent hover:bg-white"
              } ${option.enabled ? "" : "cursor-not-allowed opacity-50"}`}
              onClick={() => {
                if (option.enabled) {
                  setFieldValue("provider", option.value);
                }
              }}
              disabled={!option.enabled}
            >
              <span className="block text-xl font-semibold text-midnight">{option.title}</span>
              <span className="mt-2 block text-sm text-stone-600">{option.body}</span>
              {!option.enabled ? (
                <span className="mt-3 block text-sm font-semibold text-stone-500">Not configured on this deployment</span>
              ) : null}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="rounded-sm border border-stone-200 bg-white/50 px-6 py-6">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={values.allowPublicRecognition}
            onChange={(event) => setFieldValue("allowPublicRecognition", event.target.checked)}
            className="mt-1 h-4 w-4 rounded border-stone-300 text-burgundy"
          />
          <span>
            <span className="block font-semibold text-ink">Allow public donor recognition</span>
            <span className="mt-1 block text-sm text-stone-600">
              Your name will only be displayed on the public donor wall if this box is selected and payment is confirmed.
            </span>
          </span>
        </label>

        {values.allowPublicRecognition ? (
          <div className="mt-4">
            <InputField
              id="public-display-name"
              label="Public display name"
              value={values.publicDisplayName}
              onChange={(event) => setFieldValue("publicDisplayName", event.target.value)}
              error={errors.publicDisplayName}
              hint="Leave blank to use the donor name you entered above."
            />
          </div>
        ) : null}
      </div>

      <input
        type="text"
        name="website"
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        value={values.website}
        onChange={(event) => setFieldValue("website", event.target.value)}
      />

      <TurnstileField
        siteKey={config?.turnstileSiteKey ?? null}
        onTokenChange={(token) => setFieldValue("turnstileToken", token ?? "")}
      />

      <div className="rounded-sm border border-stone-200 bg-white/50 px-6 py-6">
        <p className="font-semibold text-midnight">Giving summary</p>
        <p className="mt-2 text-stone-700">
          {amountInCents ? formatCurrency(amountInCents) : "Enter an amount"} via{" "}
          {values.provider === "stripe" ? "Stripe checkout" : "PayPal"}
        </p>
        <p className="mt-2 text-sm text-stone-600">
          Donations are only marked as confirmed after verified server-side payment completion. Public recognition is opt-in.
        </p>
      </div>

      {configError ? <StatusBanner tone="error" title="Configuration issue" message={configError} /> : null}
      {formError ? <StatusBanner tone="error" title="Donation not started" message={formError} /> : null}
      {paypalError && values.provider === "paypal" ? (
        <StatusBanner tone="error" title="PayPal unavailable" message={paypalError} />
      ) : null}

      <div className="flex flex-col gap-4">
        <Button
          type="button"
          onClick={redirectToStripe}
          disabled={submitting || configLoading || values.provider !== "stripe" || !stripeConfigured}
        >
          {submitting && values.provider === "stripe" ? "Redirecting..." : "Continue to secure Stripe checkout"}
        </Button>
        <div className={values.provider === "paypal" ? "block" : "hidden"}>
          <div ref={paypalContainerRef} />
        </div>
      </div>

      <p className="text-sm text-stone-600">
        Card data is handled by Stripe’s hosted checkout. PayPal payments are created and captured on the server after approval.
      </p>
    </div>
  );
};
